import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { promptLibraryApi } from "@/services/promptLibraryApi";
import { PromptLibrary, PromptLibraryUsage } from "@/types/models";

// Read hooks
export function usePromptLibraries() {
  return useQuery({
    queryKey: ["prompt-libraries"],
    queryFn: () => promptLibraryApi.getPromptLibraries(),
  });
}

export function usePromptLibraryUsage() {
  return useQuery<PromptLibraryUsage[]>({
    queryKey: ["prompt-library-usage"],
    queryFn: () => promptLibraryApi.getPromptLibraryUsage(),
  });
}

export function usePromptLibrariesByTemplate(templateId: string) {
  return useQuery({
    queryKey: ["prompt-libraries", "template", templateId],
    queryFn: () => promptLibraryApi.getPromptLibrariesByTemplate(templateId),
    enabled: !!templateId,
  });
}

// Admin mutations
export function usePromptLibraryAdmin() {
  const qc = useQueryClient();
  const invalidate = () => {
    // Invalidate all prompt library queries
    qc.invalidateQueries({ queryKey: ["prompt-libraries"] });
    // Invalidate association queries
    qc.invalidateQueries({ queryKey: ["template-associations"] });
    // Also invalidate templates to update counts
    qc.invalidateQueries({ queryKey: ["templates"] });
    // Invalidate usage counts so the Usage column stays fresh
    qc.invalidateQueries({ queryKey: ["prompt-library-usage"] });
  };

  const create = useMutation({
    mutationFn: promptLibraryApi.createPromptLibrary,
    onSuccess: invalidate,
  });

  const update = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<PromptLibrary> }) =>
      promptLibraryApi.updatePromptLibrary(id, data),
    onSuccess: invalidate,
  });

  const remove = useMutation({
    mutationFn: promptLibraryApi.deletePromptLibrary,
    onSuccess: invalidate,
  });

  return { create, update, remove };
}

// Template-PromptLibrary Association hooks
export function useTemplateAssociations(templateId?: string) {
  return useQuery({
    queryKey: ["template-associations", templateId],
    queryFn: () => promptLibraryApi.getAssociations(templateId ? { templateId } : undefined),
  });
}

export function useAssociationAdmin() {
  const qc = useQueryClient();
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["template-associations"] });
    qc.invalidateQueries({ queryKey: ["prompt-libraries"] });
    qc.invalidateQueries({ queryKey: ["templates"] });
    // Invalidate usage counts so the Usage column stays fresh
    qc.invalidateQueries({ queryKey: ["prompt-library-usage"] });
  };

  const createAssociation = useMutation({
    mutationFn: promptLibraryApi.createAssociation,
    onSuccess: invalidate,
  });

  const deleteAssociation = useMutation({
    mutationFn: promptLibraryApi.deleteAssociation,
    onSuccess: invalidate,
  });

  return { createAssociation, deleteAssociation };
}
